/**
 * Hormony® custom.js — Animation controller
 * All animations respect prefers-reduced-motion.
 * All IntersectionObservers fire once only (threshold 0.15).
 * Performance: transform + opacity only, no layout-repainting props.
 * Hormony ease-out: cubic-bezier(0.25, 0.46, 0.45, 0.94)
 */

(function () {
  'use strict';

  // Mark HTML element immediately so CSS can gate opacity:0 on JS availability.
  // Sections start visible by default; this class enables the reveal animations.
  // If custom.js fails to execute, sections remain fully visible (safe default).
  document.documentElement.classList.add('js-animations');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. Hero text entrance — staggered page-load cascade ───────────────
     Targets elements with [data-hero-animate] inside .hormony-hero.
     Eyebrow → H1 → sub → body → CTAs → trust strip.
     200ms stagger per element, 600ms duration each.
     Fires immediately on DOMContentLoaded (not scroll-triggered).
  ─────────────────────────────────────────────────────────────────────── */
  function initHeroEntrance() {
    var hero = document.querySelector('.hormony-hero');
    if (!hero) return;

    var elements = hero.querySelectorAll('[data-hero-animate]');
    if (!elements.length) return;

    if (reducedMotion) {
      elements.forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    elements.forEach(function (el, i) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'opacity 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      el.style.transitionDelay = (i * 200) + 'ms';
    });

    // Small rAF to ensure styles are painted before we flip them
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        elements.forEach(function (el) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        });
      });
    });
  }

  /* ── 2. Global reveal-section observer ─────────────────────────────────
     Any element with class .reveal-section fades in on scroll.
     Sections declare their own opacity/transition in scoped CSS;
     this observer just adds .is-visible at the right moment.
     Also picks up .step-number elements for the How It Works ghost fade.
  ─────────────────────────────────────────────────────────────────────── */
  function initRevealObserver() {
    // Collect both .reveal-section AND legacy [data-scroll-reveal] elements
    var targets = document.querySelectorAll(
      '.reveal-section, [data-scroll-reveal], .step-number'
    );
    if (!targets.length) return;

    if (reducedMotion) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12 });

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ── 3. Sticky nav — solidifies at 80px scroll ──────────────────────────
     Adds .is-scrolled to the <header> / .shopify-section-header element.
     CSS in hormony.css handles the visual: transparent → frosted white.
  ─────────────────────────────────────────────────────────────────────── */
  function initStickyNav() {
    var header = document.querySelector('sticky-header') ||
                 document.querySelector('.section-header') ||
                 document.querySelector('header[class*="header"]');
    if (!header) return;

    var ticking = false;
    var THRESHOLD = 80;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          if (window.scrollY > THRESHOLD) {
            header.classList.add('is-scrolled');
          } else {
            header.classList.remove('is-scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load
  }

  /* ── 4. Smooth anchor scroll for hero secondary CTA ────────────────────
     Secondary CTA links to #hormony-products. Dawn doesn't add
     smooth-scroll by default; this ensures the jump is animated.
  ─────────────────────────────────────────────────────────────────────── */
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var id = this.getAttribute('href').slice(1);
        var target = document.getElementById(id) ||
                     document.querySelector('[data-section-id="' + id + '"]');
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  /* ── 5. Add data-hero-animate attributes to hero children ───────────────
     The Liquid file uses data-scroll-reveal and data-delay for back-compat.
     This function also wires the newer data-hero-animate pattern so
     both work without touching the Liquid.
  ─────────────────────────────────────────────────────────────────────── */
  function tagHeroElements() {
    var hero = document.querySelector('.hormony-hero__content');
    if (!hero) return;

    var animatable = [
      '.hormony-hero__eyebrow',
      '.hormony-hero__title',
      '.hormony-hero__sub',
      '.hormony-hero__body',
      '.hormony-hero__actions',
      '.hormony-hero__proof',
      '.hormony-hero__trust-strip'
    ];

    animatable.forEach(function (selector) {
      var el = hero.querySelector(selector);
      if (el) el.setAttribute('data-hero-animate', '');
    });
  }

  /* ── 6. Gradient stripe — inject 2px prismatic top bar ─────────────────
     Creates .hormony-gradient-stripe above Dawn's header. Lightweight:
     one fixed div, no repaints, pointer-events none.
  ─────────────────────────────────────────────────────────────────────── */
  function initGradientStripe() {
    var stripe = document.createElement('div');
    stripe.className = 'hormony-gradient-stripe';
    stripe.setAttribute('aria-hidden', 'true');
    document.body.insertBefore(stripe, document.body.firstChild);
  }

  /* ── 7. Quiz modal ──────────────────────────────────────────────────────
     Opens a <dialog> with an embedded iframe when any
     [data-hormony-quiz-open] element is clicked.
     The quiz URL is read from:
       1. data-quiz-url attribute on the trigger element, OR
       2. href attribute (graceful degradation if JS fails)
     Closes on: × button, backdrop click, Escape key.
     Iframe src is cleared on close to stop any audio/video.
  ─────────────────────────────────────────────────────────────────────── */
  function initQuizModal() {
    var triggers = document.querySelectorAll('[data-hormony-quiz-open]');
    if (!triggers.length) return;

    // Build the dialog once
    var dialog = document.createElement('dialog');
    dialog.id = 'hormony-quiz-modal';
    dialog.className = 'hormony-quiz-modal';
    dialog.setAttribute('aria-label', 'Hormony quiz');
    dialog.innerHTML =
      '<div class="hormony-quiz-modal__inner">' +
        '<button class="hormony-quiz-modal__close" aria-label="Close quiz">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
            '<path d="M18 6L6 18M6 6l12 12"/>' +
          '</svg>' +
        '</button>' +
        '<iframe id="hormony-quiz-iframe" class="hormony-quiz-modal__iframe" src="" frameborder="0" allow="fullscreen" title="Hormony quiz"></iframe>' +
      '</div>';
    document.body.appendChild(dialog);

    var iframe = dialog.querySelector('#hormony-quiz-iframe');

    function openModal(url) {
      if (url && url !== '#') {
        iframe.src = url;
      }
      dialog.showModal();
      dialog.focus();
    }

    function closeModal() {
      dialog.close();
      // Small delay so close animation plays before src reset
      setTimeout(function () { iframe.src = ''; }, 350);
    }

    // Wire triggers
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var url = this.getAttribute('data-quiz-url') ||
                  this.getAttribute('href') || '';
        openModal(url);
      });
    });

    // Close button
    dialog.querySelector('.hormony-quiz-modal__close').addEventListener('click', closeModal);

    // Backdrop click (the dialog element itself, not .inner)
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) closeModal();
    });

    // Escape key (browsers fire this natively on <dialog> but we also
    // want to clear the iframe src)
    dialog.addEventListener('cancel', function () {
      setTimeout(function () { iframe.src = ''; }, 350);
    });
  }

  /* ── 8. Stat counter — count-up animation on scroll ───────────────────
     Targets [data-counter] elements. Reads data-target for the final
     value and data-duration (ms, default 1200) for speed.
     Fires once on IntersectionObserver threshold 0.5.
  ─────────────────────────────────────────────────────────────────────── */
  function initCounters() {
    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    if (reducedMotion) {
      counters.forEach(function (el) {
        el.textContent = el.getAttribute('data-target') || el.textContent;
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseFloat(el.getAttribute('data-target') || el.textContent);
        var duration = parseInt(el.getAttribute('data-duration') || '1200', 10);
        var start = performance.now();
        var suffix = el.getAttribute('data-suffix') || '';
        var prefix = el.getAttribute('data-prefix') || '';
        var isDecimal = String(target).indexOf('.') !== -1;

        function tick(now) {
          var elapsed = now - start;
          var progress = Math.min(elapsed / duration, 1);
          // ease-out cubic
          var eased = 1 - Math.pow(1 - progress, 3);
          var current = target * eased;
          el.textContent = prefix + (isDecimal ? current.toFixed(1) : Math.round(current)) + suffix;
          if (progress < 1) requestAnimationFrame(tick);
        }

        requestAnimationFrame(tick);
        observer.unobserve(el);
      });
    }, { threshold: 0.5 });

    counters.forEach(function (el) {
      el.setAttribute('data-target', el.getAttribute('data-target') || el.textContent.trim());
      observer.observe(el);
    });
  }

  /* ── Init ────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    tagHeroElements();
    initHeroEntrance();
    initRevealObserver();
    initCounters();
    initStickyNav();
    initSmoothAnchors();
    initGradientStripe();
    initQuizModal();
  });
})();
